import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, Settings } from "lucide-react";
import { AccountAvatar } from "@/features/accounts/AccountAvatar";
import { UnreadCountBadge } from "@/features/inbox/UnreadCountBadge";
import { MobileButton } from "@/mobile/components/ui/MobileButton";
import { MobileDrawer } from "@/mobile/components/ui/MobileDrawer";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import type { AccountSettingsFocus } from "@/features/accounts/accountSettingsFocus";
import { MobileNavDrawerSuiteFooter } from "@/mobile/components/nav/MobileNavDrawerSuiteFooter";
import { getAccountDisplayLabel } from "@/lib/mail/accountDisplayLabel";
import { cn } from "@/lib/cn";
import type { MailAccountSummary, MailFolder } from "@/lib/types/mail";

type Props = {
  open: boolean;
  accounts: MailAccountSummary[];
  accountId: number | null;
  folder: string;
  navFolders: MailFolder[];
  extraNavFolders?: MailFolder[];
  virtualEntries?: { name: string; label: string; badge?: number }[];
  onClose: () => void;
  onSelectAccount: (id: number) => void;
  onSelectFolder: (name: string) => void;
  onOpenSettings?: (focus?: AccountSettingsFocus) => void;
  onAddAccount?: () => void;
  foldersLoading?: boolean;
  settingsVersion?: number;
};

function UnreadCount({ count }: { count: number }) {
  return <UnreadCountBadge count={count} variant="soft" />;
}

export function MobileNavDrawer({
  open,
  accounts,
  accountId,
  folder,
  navFolders,
  extraNavFolders = [],
  virtualEntries = [],
  onClose,
  onSelectAccount,
  onSelectFolder,
  onOpenSettings,
  onAddAccount,
  foldersLoading = false,
  settingsVersion = 0,
}: Props) {
  void settingsVersion;
  const [othersOpen, setOthersOpen] = useState(false);
  const accountLabel = useMemo(() => {
    const acc = accounts.find((a) => a.id === accountId);
    if (!acc) return "Compte";
    return getAccountDisplayLabel(acc);
  }, [accounts, accountId, settingsVersion]);

  return (
    <MobileDrawer open={open} brand onClose={onClose}>
      <div className="flex min-h-full flex-col">
        <div className="flex flex-col px-2 py-2">
          <p className="px-2 pb-1 text-[11px] font-medium text-[var(--mail-muted)]">Boîtes</p>
          <div className="flex flex-col">
            {accounts.map((acc) => {
              const displayLabel = getAccountDisplayLabel(acc);
              const selected = acc.id === accountId;
              return (
                <MobileButton
                  key={acc.id}
                  variant="ghost"
                  className={cn(
                    "h-auto w-full justify-start gap-2.5 rounded-lg px-2 py-2 text-left",
                    selected && "bg-[var(--mail-accent-soft)] text-[var(--mail-accent-dark)]"
                  )}
                  onClick={() => onSelectAccount(acc.id)}
                >
                  <AccountAvatar account={acc} size="sm" />
                  <span className="truncate text-sm font-medium">{displayLabel}</span>
                </MobileButton>
              );
            })}
          </div>
        </div>

        <div className="mx-3 border-t border-[var(--mail-border-soft)]" />

        <div className="flex flex-col px-2 py-2">
          <p className="px-2 pb-1 text-[11px] font-medium text-[var(--mail-muted)]">
            Dossiers — {accountLabel}
          </p>
          {foldersLoading ? (
            <div className="px-2 py-1">
              <MailLoadingState variant="folders" layout="inline" />
            </div>
          ) : null}
          <div className="flex flex-col">
            {virtualEntries.map((entry) => (
              <MobileButton
                key={entry.name}
                variant="ghost"
                className={cn(
                  "h-auto w-full justify-between gap-2 rounded-lg px-2 py-2 text-left",
                  entry.name === folder &&
                    "bg-[var(--mail-accent-soft)] font-medium text-[var(--mail-accent-dark)]"
                )}
                onClick={() => {
                  onSelectFolder(entry.name);
                  onClose();
                }}
              >
                <span className="truncate text-sm">{entry.label}</span>
                <UnreadCount count={entry.badge ?? 0} />
              </MobileButton>
            ))}
            {navFolders.map((f) => (
              <MobileButton
                key={f.name}
                variant="ghost"
                className={cn(
                  "h-auto w-full justify-between gap-2 rounded-lg px-2 py-2 text-left",
                  f.name === folder &&
                    "bg-[var(--mail-accent-soft)] font-medium text-[var(--mail-accent-dark)]"
                )}
                onClick={() => {
                  onSelectFolder(f.name);
                  onClose();
                }}
              >
                <span className="truncate text-sm">{f.label}</span>
                <UnreadCount count={f.unread ?? 0} />
              </MobileButton>
            ))}
            {extraNavFolders.length > 0 ? (
              <div className="pt-0.5">
                <MobileButton
                  variant="ghost"
                  className="h-auto w-full justify-start gap-1.5 rounded-lg px-2 py-2 text-left text-[var(--mail-muted)]"
                  onClick={() => setOthersOpen((open) => !open)}
                >
                  {othersOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  <span className="text-sm">Tous les dossiers</span>
                  <span className="text-[11px] tabular-nums">({extraNavFolders.length})</span>
                </MobileButton>
                {othersOpen
                  ? extraNavFolders.map((f) => (
                      <MobileButton
                        key={f.name}
                        variant="ghost"
                        className={cn(
                          "h-auto w-full justify-between gap-2 rounded-lg px-2 py-2 pl-7 text-left",
                          f.name === folder &&
                            "bg-[var(--mail-accent-soft)] font-medium text-[var(--mail-accent-dark)]"
                        )}
                        onClick={() => {
                          onSelectFolder(f.name);
                          onClose();
                        }}
                      >
                        <span className="truncate text-sm">{f.label}</span>
                        <UnreadCount count={f.unread ?? 0} />
                      </MobileButton>
                    ))
                  : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-auto border-t border-[var(--mail-border-soft)] px-2 py-2">
          {onOpenSettings ? (
            <MobileButton
              variant="ghost"
              className="h-auto w-full justify-start gap-2 rounded-lg px-2 py-2 text-left"
              onClick={() => onOpenSettings?.("navigation")}
            >
              <Settings className="h-4 w-4 shrink-0 text-[var(--mail-muted)]" aria-hidden />
              <span className="text-sm">Paramètres du compte</span>
            </MobileButton>
          ) : null}
          {onAddAccount ? (
            <MobileButton
              variant="ghost"
              className="h-auto w-full justify-start gap-2 rounded-lg px-2 py-2 text-left text-[var(--mail-accent)]"
              onClick={onAddAccount}
              aria-label="Ajouter une boîte mail"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              <span className="text-sm font-medium">Ajouter une boîte</span>
            </MobileButton>
          ) : null}
          <div className="my-1 border-t border-[var(--mail-border-soft)]" />
          <MobileNavDrawerSuiteFooter />
        </div>
      </div>
    </MobileDrawer>
  );
}
